import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { AxiosResponse } from "axios";
import nameof from "ts-nameof.macro";
import { API_RESPONSIBILITY_PREFIX } from "config/api-consts";
import { Responsibility, ResponsibilityFilter } from "models/Responsibility";
import { Status, StatusFilter } from "models/Status";
import { AppUser, AppUserFilter } from "models/AppUser";
import { Permission, PermissionFilter } from "models/Permission";
import { Menu, MenuFilter } from "models/Menu";
import { FieldModel, FieldModelFilter } from "models/FieldModel";
import {
  PermissionOperator,
  PermissionOperatorFilter,
} from "models/PermissionOperator";
import { Organization, OrganizationFilter } from "models/Organization";
import { Site, SiteFilter } from "models/Site";
import { MasterData, MasterDataFilter } from "models/MasterData";
import { ActionType, ActionTypeFilter } from "models/ActionType";
import { Role, RoleFilter } from "models/Role";
import {
  ResponsibilityAppUserGroupingMappings,
  ResponsibilityAppUserGroupingMappingsFilter,
} from "models/ResponsibilityAppUserGroupingMappings";
import { SelectStatus, SelectStatusFilter } from "models/SelectSatus";
import { Province, ProvinceFilter } from "models/Province";
import { RequestHistory, RequestHistoryFilter } from "models/RequestHistory";

export type KeyType = string | number;

export class ResponsibilityRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(API_RESPONSIBILITY_PREFIX, BASE_API_URL).href;
  }

  public count = (
    responsibilityFilter?: ResponsibilityFilter
  ): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), responsibilityFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public list = (
    responsibilityFilter?: ResponsibilityFilter
  ): Observable<Responsibility[]> => {
    return this.http
      .post<Responsibility[]>(
        kebabCase(nameof(this.list)),
        responsibilityFilter
      )
      .pipe(Repository.responseMapToList<Responsibility>(Responsibility));
  };

  public listRequestHistory = (
    requestHistoryFilter?: RequestHistoryFilter
  ): Observable<RequestHistory[]> => {
    return this.http
      .post<RequestHistory[]>(
        kebabCase(nameof(this.listRequestHistory)),
        requestHistoryFilter
      )
      .pipe(Repository.responseMapToList<RequestHistory>(RequestHistory));
  };

  public countRequestHistory = (
    requestHistoryFilter?: RequestHistoryFilter
  ): Observable<number> => {
    return this.http
      .post<number>(
        kebabCase(nameof(this.countRequestHistory)),
        requestHistoryFilter
      )
      .pipe(Repository.responseDataMapper<number>());
  };

  public getRequestHistory = (
    versionId: number | string
  ): Observable<RequestHistory> => {
    return this.http
      .post<RequestHistory>(kebabCase(nameof(this.getRequestHistory)), {
        versionId,
      })
      .pipe(Repository.responseMapToModel<RequestHistory>(RequestHistory));
  };

  public countAppUserGrouping = (
    responsibilityAppUserGroupingMappingsFilter?: ResponsibilityAppUserGroupingMappingsFilter
  ): Observable<number> => {
    return this.http
      .post<number>(
        kebabCase(nameof(this.countAppUserGrouping)),
        responsibilityAppUserGroupingMappingsFilter
      )
      .pipe(Repository.responseDataMapper<number>());
  };

  public listAppUserGrouping = (
    responsibilityAppUserGroupingMappingsFilter?: ResponsibilityAppUserGroupingMappingsFilter
  ): Observable<ResponsibilityAppUserGroupingMappings[]> => {
    return this.http
      .post<ResponsibilityAppUserGroupingMappings[]>(
        kebabCase(nameof(this.listAppUserGrouping)),
        responsibilityAppUserGroupingMappingsFilter
      )
      .pipe(
        Repository.responseMapToList<ResponsibilityAppUserGroupingMappings>(
          ResponsibilityAppUserGroupingMappings
        )
      );
  };

  public get = (id: number | string): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(kebabCase(nameof(this.get)), { id })
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };

  public getMenu = (id: number | string): Observable<Menu> => {
    return this.http
      .post<Menu>(kebabCase(nameof(this.getMenu)), { id })
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };
  public getPermissionDraft = (per: Permission): Observable<Permission> => {
    return this.http
      .post<Permission>(kebabCase(nameof(this.getPermissionDraft)), per)
      .pipe(Repository.responseMapToModel<Permission>(Permission));
  };

  public create = (
    responsibility: Responsibility
  ): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(kebabCase(nameof(this.create)), responsibility)
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };

  public clone = (id: number | string): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(kebabCase(nameof(this.clone)), { id })
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };

  public update = (
    responsibility: Responsibility
  ): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(kebabCase(nameof(this.update)), responsibility)
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };

  public delete = (
    responsibility: Responsibility
  ): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(kebabCase(nameof(this.delete)), responsibility)
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };

  public bulkDelete = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public save = (
    responsibility: Responsibility
  ): Observable<Responsibility> => {
    return responsibility.id
      ? this.update(responsibility)
      : this.create(responsibility);
  };

  public singleListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public singleListAppUser = (
    appUserFilter: AppUserFilter
  ): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.singleListAppUser)), appUserFilter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };
  public filterListAppUser = (
    appUserFilter: AppUserFilter
  ): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.filterListAppUser)), appUserFilter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };
  public singleListPermission = (
    permissionFilter: PermissionFilter
  ): Observable<Permission[]> => {
    return this.http
      .post<Permission[]>(
        kebabCase(nameof(this.singleListPermission)),
        permissionFilter
      )
      .pipe(Repository.responseMapToList<Permission>(Permission));
  };
  public filterListPermission = (
    permissionFilter: PermissionFilter
  ): Observable<Permission[]> => {
    return this.http
      .post<Permission[]>(
        kebabCase(nameof(this.filterListPermission)),
        permissionFilter
      )
      .pipe(Repository.responseMapToList<Permission>(Permission));
  };
  public singleListMenu = (menuFilter: MenuFilter): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.singleListMenu)), menuFilter)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };
  public singleListSite = (siteFilter: SiteFilter): Observable<Site[]> => {
    return this.http
      .post<Site[]>(kebabCase(nameof(this.singleListSite)), siteFilter)
      .pipe(Repository.responseMapToList<Site>(Site));
  };
  public filterListSite = (siteFilter: SiteFilter): Observable<Site[]> => {
    return this.http
      .post<Site[]>(kebabCase(nameof(this.filterListSite)), siteFilter)
      .pipe(Repository.responseMapToList<Site>(Site));
  };

  public singleListActionType = (
    filter?: ActionTypeFilter
  ): Observable<ActionType[]> => {
    return this.http
      .post<ActionType[]>(kebabCase(nameof(this.singleListActionType)), filter)
      .pipe(Repository.responseMapToList<ActionType>(ActionType));
  };

  public singleListOrganization = (
    filter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        filter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public singleListProvince = (
    filter: ProvinceFilter
  ): Observable<Province[]> => {
    return this.http
      .post<Province[]>(kebabCase(nameof(this.singleListProvince)), filter)
      .pipe(Repository.responseMapToList<Province>(Province));
  };
  public singleListField = (
    filter: FieldModelFilter
  ): Observable<FieldModel[]> => {
    return this.http
      .post<FieldModel[]>(kebabCase(nameof(this.singleListField)), filter)
      .pipe(Repository.responseMapToList<FieldModel>(FieldModel));
  };

  public singleListMasterEntity = (
    filter: FieldModelFilter
  ): Observable<FieldModel[]> => {
    return this.http
      .post<FieldModel[]>(
        kebabCase(nameof(this.singleListMasterEntity)),
        filter
      )
      .pipe(Repository.responseMapToList<FieldModel>(FieldModel));
  };

  public singleListPermissionOperator = (
    filter: PermissionOperatorFilter
  ): Observable<PermissionOperator[]> => {
    return this.http
      .post<PermissionOperator[]>(
        kebabCase(nameof(this.singleListPermissionOperator)),
        filter
      )
      .pipe(
        Repository.responseMapToList<PermissionOperator>(PermissionOperator)
      );
  };
  public filterListMenu = (menuFilter: MenuFilter): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.filterListMenu)), menuFilter)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };
  public singleListMasterData = (
    masterDataFilter: MasterDataFilter
  ): Observable<MasterData[]> => {
    return this.http
      .post<MasterData[]>(
        kebabCase(nameof(this.singleListMasterData)),
        masterDataFilter
      )
      .pipe(Repository.responseMapToList<MasterData>(MasterData));
  };

  public singleListRole = (roleFilter: RoleFilter): Observable<Role[]> => {
    return this.http
      .post<Role[]>(kebabCase(nameof(this.singleListRole)), roleFilter)
      .pipe(Repository.responseMapToList<Role>(Role));
  };

  public countAppUser = (appUserFilter: AppUserFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.countAppUser)), appUserFilter)
      .pipe(Repository.responseDataMapper<number>());
  };
  public listAppUser = (
    appUserFilter: AppUserFilter
  ): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.listAppUser)), appUserFilter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };

  public filterListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.filterListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public filterListSelectStatus = (): Observable<SelectStatus[]> => {
    return this.http
      .post<SelectStatus[]>(
        kebabCase(nameof(this.filterListSelectStatus)),
        new SelectStatusFilter()
      )
      .pipe(Repository.responseMapToList<SelectStatus>(SelectStatus));
  };

  public assignAppUser = (
    responsibility: Responsibility
  ): Observable<Responsibility> => {
    return this.http
      .post<Responsibility>(
        kebabCase(nameof(this.assignAppUser)),
        responsibility
      )
      .pipe(Repository.responseMapToModel<Responsibility>(Responsibility));
  };
  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.http.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };
}

export const responsibilityRepository = new ResponsibilityRepository();
